import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { track } from '@/lib/analytics';
import { FiArrowLeft, FiMapPin, FiPlus } from 'react-icons/fi';
import { Button, Card } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import type { CheckIn, CheckInListResponse, HouseholdResponse, UserResponse } from '@/lib/types';

function CheckInsPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    track('Check Ins List Screen');
  }, []);

  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: async () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  const householdQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => apiJson<HouseholdResponse[]>(getToken, '/v1/households'),
  });

  const checkInsQuery = useQuery({
    queryKey: ['check-ins'],
    queryFn: async () => apiJson<CheckInListResponse>(getToken, '/v1/check-ins'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (checkInId: number) => {
      return apiJson(getToken, `/v1/check-ins/${checkInId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: async () => {
      track('Check In Deleted');
      await queryClient.invalidateQueries({ queryKey: ['check-ins'] });
    },
    onError: (err: unknown) => {
      let message = 'Failed to delete check-in.';
      if (err instanceof ApiError && err.bodyText) {
        try {
          message =
            (JSON.parse(err.bodyText) as { detail?: string }).detail || message;
        } catch {
          /* ignore */
        }
      }
      window.alert(message);
    },
  });

  const handleDelete = (checkInId: number) => {
    if (
      !window.confirm(
        'Delete this check-in? This action cannot be undone.'
      )
    ) {
      return;
    }
    deleteMutation.mutate(checkInId);
  };

  const currentUserId = userQuery.data?.id;
  const members = householdQuery.data?.[0]?.members ?? [];

  const getMemberName = (userId: number): string => {
    if (userId === currentUserId) return 'You';
    const member = members.find((m) => m.user_id === userId);
    return member?.first_name || 'Co-parent';
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const checkIns = checkInsQuery.data?.check_ins ?? [];

  const mapsUrl = (c: CheckIn) =>
    `https://www.google.com/maps?q=${c.latitude},${c.longitude}`;

  return (
    <div className='min-h-screen bg-sky-light pb-24'>
      <header className='bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4 flex items-center justify-between gap-4'>
          <Link
            to='/app/calendar'
            className='inline-flex items-center gap-2 text-primary hover:underline font-semibold'
          >
            <FiArrowLeft /> Calendar
          </Link>
          <Link
            to='/app/calendar/add-check-in'
            className='inline-flex items-center gap-1 text-sm font-bold text-primary'
          >
            <FiPlus className='w-5 h-5' />
            New
          </Link>
        </div>
      </header>

      <div className='max-w-lg mx-auto px-6 py-6 space-y-4'>
        <h1
          className='text-2xl font-light text-primary'
          style={{
            fontFamily:
              "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          GPS check-ins
        </h1>

        {checkInsQuery.isLoading && (
          <div className='flex justify-center py-12'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        )}

        {!checkInsQuery.isLoading && checkIns.length === 0 && (
          <Card className='text-center py-10 text-gray-600'>
            <FiMapPin className='w-10 h-10 mx-auto text-gray-300' />
            <p className='mt-4 font-bold text-gray-900'>No check-ins yet</p>
            <p className='text-sm mt-2'>Record your location at pickups and drop-offs.</p>
            <Link to='/app/calendar/add-check-in' className='inline-block mt-6'>
              <Button>Add check-in</Button>
            </Link>
          </Card>
        )}

        {checkIns.map((item) => {
          const isOwner = item.creator_user_id === currentUserId;
          return (
            <Card key={item.id} className='space-y-3'>
              <a
                href={mapsUrl(item)}
                target='_blank'
                rel='noopener noreferrer'
                className='block h-36 rounded-xl bg-gray-100 border border-gray-200 overflow-hidden relative'
              >
                <div className='absolute inset-0 flex items-center justify-center text-gray-400 text-sm font-semibold gap-2'>
                  <FiMapPin className='w-5 h-5' />
                  Open map
                </div>
              </a>
              <div className='flex justify-between items-start gap-2'>
                <div>
                  <p className='font-bold text-gray-900'>
                    {formatDate(item.created_at)} · {formatTime(item.created_at)}
                  </p>
                  <p className='text-sm text-gray-500 mt-1'>
                    {getMemberName(item.creator_user_id)}
                  </p>
                  {item.address ? (
                    <p className='text-sm text-gray-700 mt-2'>{item.address}</p>
                  ) : (
                    <p className='text-xs text-gray-500 mt-2 font-mono'>
                      {item.latitude.toFixed(5)}, {item.longitude.toFixed(5)}
                    </p>
                  )}
                  {item.note ? (
                    <p className='text-sm text-gray-600 mt-2'>{item.note}</p>
                  ) : null}
                </div>
                {isOwner && (
                  <button
                    type='button'
                    onClick={() => handleDelete(item.id)}
                    disabled={deleteMutation.isPending}
                    className='text-sm font-semibold text-red-600 shrink-0'
                  >
                    Delete
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/calendar/check-ins')({
  component: CheckInsPage,
});
